import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Space,
  Stack,
} from "@mantine/core";
import { Text } from "../ui/Text";
import { PlaylistCard } from "../playlists/PlaylistCard";
import {
  IconExternalLink,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useStore } from "@/store";
import { useState, useEffect, useMemo } from "react";
import { PlaylistService } from "@/services/PlaylistService";
import { IPlaylistConfig } from "@/types/PlaylistTypes";
import { Link } from "../ui/Link";
import { PlaylistScale } from "../playlists/PlaylistScale";
import { PlaylistVideo } from "../playlists/PlaylistVideo";
import { AssignmentService } from "@/services/AssignmentService";
import {
  useActiveAssignmentId,
  useActiveAssignment,
} from "@/store/selectors/assignmentSelectors";
import { CopyPlaylistLink } from "../playlists/CopyPlaylistLink";
export function PlaylistPreview({ playlistId }: { playlistId: string }) {
  const [playlist, setPlaylist] = useState<IPlaylistConfig | null>(null);
  const activeAssignmentId = useActiveAssignmentId();
  const setActivePlaylistId = useStore(
    (state) => state.partnerApp.setActivePlaylistId,
  );
  const updatePlaylistState = useStore(
    (state) => state.playlists.updatePlaylistsState,
  );

  const activeAssignment = useActiveAssignment();
  //   const playlist = useActivePlaylist(playlistId);

  const isPlaylistInActiveAssignment = useMemo(() => {
    return (activeAssignment?.playlists || {})[playlistId];
  }, [activeAssignment, playlistId]);

  const isAssignmentEmpty = useMemo(() => {
    return Object.keys(activeAssignment?.playlists || {}).length === 0;
  }, [activeAssignment]);

  useEffect(() => {
    async function handleFetchPlaylist() {
      const playlist = await PlaylistService.fetchPlaylistById(playlistId);
      setPlaylist(playlist);
    }

    if (playlistId) {
      handleFetchPlaylist();
    }
  }, [playlistId, updatePlaylistState]);

  console.log("playlist", playlist);

  const PlaylistStepsPreview = useMemo(() => {
    return (
      <Stack gap={40} px="lg" pb={100}>
        {Object.entries(playlist?.steps || {})
          .filter(([stepKey]) => {
            return ![
              "playlist-intro",
              "interest-response-before",
              "interest-response-after",
            ].includes(stepKey);
          })
          .map(([stepKey, step]) => {
            let content = null;
            const isAfterStep = stepKey.endsWith("-after");
            const stepTitle =
              isAfterStep && typeof step.title === "string"
                ? (step.title || "").split("?")[0].trim()
                : step.title;

            switch (step.stepType) {
              case "scale-1-to-5":
                content = (
                  <PlaylistScale
                    id={stepKey}
                    playlistId={playlistId}
                    lowerText={step.stepParams?.lowerText || null}
                    upperText={step.stepParams?.upperText || null}
                    readOnly
                  />
                );
                break;
              case "video":
                content = (
                  <Stack gap={0} p={0}>
                    <PlaylistVideo
                      advizerId={step.stepParams?.advizerId}
                      videoQuestionNumber={step.stepParams?.videoQuestionNumber}
                      minimal
                    />
                  </Stack>
                );
                break;
              case "playlist-conclusion":
                content = (
                  <Stack>
                    <Text titleStyle="h5" lh={1.2}>
                      {stepTitle}
                    </Text>
                  </Stack>
                );
                break;
            }

            return (
              <Stack key={stepKey} gap="xs">
                <Text titleStyle="h4" lh={1.2} c="text.8" fw={500}>
                  {/* Step {index + 1}:  */}
                  {stepTitle}
                  {isAfterStep ? " " : ""}
                  {isAfterStep ? (
                    <span style={{ textDecoration: "underline" }}>
                      after watching the playlist?
                    </span>
                  ) : null}
                </Text>
                {content}
              </Stack>
            );
          })}
      </Stack>
    );
  }, [playlist?.steps, playlistId]);

  const Actions = useMemo(() => {
    if (!activeAssignment?.id) return null;
    return (
      <>
        <Text
          titleStyle="h4"
          lh={1.2}
          px="lg"
          style={{ textDecoration: "underline" }}
        >
          Actions
        </Text>

        <Group gap="xs" pb="md" pt="sm" px="lg">
          {activeAssignmentId && (
            <Button
              variant="filled"
              size="xs"
              radius="xl"
              className={isAssignmentEmpty ? "pulse-animation-light" : ""}
              color={isPlaylistInActiveAssignment ? "red" : "primary"}
              leftSection={
                isPlaylistInActiveAssignment ? (
                  <IconTrash size={16} />
                ) : (
                  <IconPlus size={16} />
                )
              }
              onClick={() => {
                if (isPlaylistInActiveAssignment) {
                  AssignmentService.removePlaylistFromAssignment({
                    assignmentId: activeAssignment.id,
                    playlistId,
                  });
                } else {
                  AssignmentService.addPlaylistToAssignment({
                    assignmentId: activeAssignment.id,
                    playlistId,
                  });
                }
              }}
            >
              {isPlaylistInActiveAssignment ? "Remove" : "Assign"}
            </Button>
          )}
          <CopyPlaylistLink
            playlistId={playlistId}
            size="sm"
            context="Playlist Preview - Copy playlist link"
          />
          <Link to={`/playlists/${playlistId}`} target="_blank">
            <Button
              variant="light"
              size="xs"
              radius="xl"
              color="primary"
              leftSection={<IconExternalLink size={16} />}
            >
              Student view
            </Button>
          </Link>
        </Group>
      </>
    );
  }, [
    activeAssignment?.id,
    activeAssignmentId,
    isAssignmentEmpty,
    isPlaylistInActiveAssignment,
    playlistId,
  ]);

  return (
    <Stack gap={0} w="100%" p={0} pt={0} bg="background.1">
      <Group
        justify="flex-end"
        gap="xs"
        align="center"
        p={0}
        pt="xs"
        px="xs"
        pos="sticky"
        top={0}
        // bg="background.0"
        h={24}
        style={{
          zIndex: 10,
        }}
      >
        <ActionIcon
          variant="light"
          size="md"
          radius="xl"
          color="text.6"
          style={{ zIndex: 100 }}
          onClick={() => setActivePlaylistId(null)}
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>
      <Space h="xs" />

      <Stack gap={0} px="lg" pr="xl">
        <PlaylistCard
          playlistId={playlistId}
          isPreview
          advizerQuoteLineClamp={5}
        />
      </Stack>
      <Space h="md" />
      <Divider w="100%" />
      <Space h="md" />
      {Actions}
      <Divider w="100%" />
      <Space h="xl" />
      <Text
        titleStyle="h4"
        lh={1}
        px="lg"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          textDecoration: "underline",
        }}
      >
        {/* <IconPlaylist size={24} /> */}
        Playlist Preview
      </Text>
      <Space h="lg" />

      {PlaylistStepsPreview}
    </Stack>
  );
}
